"use client";

import {NeoAGgrid} from "@/components/table/neo-table/neo-ag-grid";
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiTrendingUp,
  FiXCircle
} from "react-icons/fi";

type PaymentMethod = "UPI" | "Card" | "Cash";
type PaymentStatus = "Paid" | "Cancelled";

type PassengerPayment = {
  id: string;
  name: string;
  initials: string;
  avatarColor: string;
  pickupStop: string;
  dropStop: string;
  method: PaymentMethod | "-";
  amount: number;
  paidAt: string;
  paymentDate: string;
  status: PaymentStatus;
};

export type TransactionTripDetail = {
  id: string;
  tripDate: string;
  shuttleInfo: string;
  passengers: number;
  amountPaid: number;
  from: string;
  to: string;
  insideStops: string;
  departure: string;
  arrival: string;
  passengersCancelled: number;
  payments: PassengerPayment[];
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  }).format(value);

const formatShuttleTitle = (value: string) =>
  value.replace(/^Shuttle\s+#?1/i, "TN ").trim();

const ModalSummaryCard = ({
  label,
  value,
  icon,
  iconClass
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconClass: string;
}) => (
  <div className="min-w-[170px] rounded-xl border border-slate-200 bg-white p-3">
    <div className="flex flex-row items-center justify-between">
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-lg ${iconClass}`}
      >
        {icon}
      </div>
      <div className=" text-lg font-semibold text-slate-900">{value}</div>
    </div>
    <div className="mt-1 text-sm  text-slate-600">{label}</div>
  </div>
);

export const TransactionDetailView = ({
  trip
}: {
  trip: TransactionTripDetail;
}) => {
  const modalPassengers = trip.payments.length
    ? trip.payments
    : [
        {
          id: "fallback-1",
          name: "Trip passenger data will appear here",
          initials: "TP",
          avatarColor: "bg-slate-100 text-slate-600",
          pickupStop: trip.from,
          dropStop: trip.to,
          method: "-" as const,
          amount: 0,
          paidAt: "-",
          paymentDate: trip.tripDate,
          status: "Cancelled" as const
        }
      ];

  const billingTable = {
    columns: [
      {uid: "name", name: "Passenger"},
      {uid: "pickupStop", name: "Pickup Stop"},
      {uid: "dropStop", name: "Drop Stop"},
      {uid: "method", name: "Method"},
      {uid: "amountLabel", name: "Amount"},
      {uid: "paidAt", name: "Paid At"},
      {uid: "paymentDate", name: "Payment Date"},
      {uid: "status", name: "Status"}
    ],
    activeColumns: [
      "name",
      "pickupStop",
      "dropStop",
      "method",
      "amountLabel",
      "paidAt",
      "paymentDate",
      "status"
    ],
    tabledatas: modalPassengers.map(payment => ({
      ...payment,
      amountLabel: formatCurrency(payment.amount)
    })),
    style: {
      height: "h-[320px]"
    },
    meta: {
      id: 0,
      title: "",
      description: "",
      hasSearch: true,
      hasUpload: false,
      hasDownload: false,
      hasAdd: false,
      hasActions: false,
      hasCheckbox: false,
      hasFilter: false,
      hasHeader: true,
      hasFooter: false,
      hasPagination: false,
      hasCreate: false
    },
    url: {
      page: {},
      api: {
        get: ""
      }
    }
  };

  return (
    <div className="min-h-[calc(100vh-63px)] rounded-2xl bg-white p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xl font-semibold text-slate-900">
            Billing history : {formatShuttleTitle(trip.shuttleInfo)}
          </div>
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="mt-3 flex flex-wrap gap-2.5">
          <ModalSummaryCard
            label="Total amount"
            value={formatCurrency(trip.amountPaid)}
            icon={<FiTrendingUp className="h-4 w-4" />}
            iconClass="bg-emerald-50 text-emerald-600"
          />
          <ModalSummaryCard
            label="Passengers travelled"
            value={String(trip.passengers)}
            icon={<FiCalendar className="h-4 w-4" />}
            iconClass="bg-blue-50 text-blue-600"
          />
          <ModalSummaryCard
            label="Passengers cancelled"
            value={String(trip.passengersCancelled)}
            icon={<FiXCircle className="h-4 w-4" />}
            iconClass="bg-rose-50 text-rose-600"
          />
        </div>

        <div className="mt-3 w-fit rounded-2xl border border-slate-200 bg-gray-50 p-4">
          <div className="flex flex-wrap  items-center gap-x-4 gap-y-3">
            <div className="shrink-0 border-r border-slate-200 pr-4">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                <FiMapPin className="h-3.5 w-3.5" />
                <span>From</span>
              </div>
              <div className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-800">
                {trip.from}
              </div>
            </div>

            <div className="shrink-0 border-r border-slate-200 pr-4">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                <FiMapPin className="h-3.5 w-3.5" />
                <span>To</span>
              </div>
              <div className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-800">
                {trip.to}
              </div>
            </div>

            <div className="shrink-0 border-r border-slate-200 pr-4">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                <FiClock className="h-3.5 w-3.5" />
                <span>Departure</span>
              </div>
              <div className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-800">
                {trip.departure}
              </div>
            </div>

            <div className="shrink-0">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                <FiClock className="h-3.5 w-3.5" />
                <span>Arrival</span>
              </div>
              <div className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-800">
                {trip.arrival}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
        {modalPassengers.length > 0 ? (
          <div className="h-[320px] w-full">
            <NeoAGgrid table={billingTable} />
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-xs text-slate-500">
            No billing history rows matched your search.
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-600">
        <button className="rounded-lg border border-slate-200 px-2.5 py-0.5 font-medium text-slate-900">
          1
        </button>
        <button className="px-1.5">2</button>
        <button className="px-1.5">3</button>
        <button className="px-1.5">4</button>
      </div>
    </div>
  );
};
